import { html, unsafeCSS, css } from 'lit';
import { property } from 'lit/decorators.js';
import tailwindCSS from '../../../index.css?inline';
import '../forms/form-render';
import { TemplateMixin } from '../../../core/mixins/template-mixin';
import './section-page';
import { PaymentsPageBase } from './base/payments-page.base';

export class PaymentsPage extends TemplateMixin(PaymentsPageBase) {
  static readonly is = 'payments-page';
  
  static styles = [
    unsafeCSS(tailwindCSS),
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }

      /* Dialog styles remain the same */
      .dialog-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        backdrop-filter: blur(2px);
      }

      .custom-dialog {
        background: white;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        max-width: 90vw;
        max-height: 90vh;
        min-width: 300px;
        min-height: 200px;
        overflow: auto;
        animation: dialog-appear 0.3s ease-out;
      }

      @keyframes dialog-appear {
        from {
          opacity: 0;
          transform: scale(0.9) translateY(-10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }

      .main-content {
        position: relative;
        z-index: 1;
      }

      .dialog-open {
        overflow: hidden;
        height: 100vh;
      }

      /* New styles for the template */
      .outer-container {
        display: flex;
        background: transparent;
        border-radius: var(--CornerSmall, 8px);
        box-shadow: none;
        overflow: visible;
        position: relative;
      }

      .content-wrapper {
        padding: 0 20px;
        flex: 1 1 0%;
        height: 100%;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        border: 1px solid #E8EBF1;
        box-shadow: 0px 16px 48px #E3EBFB;
        backdrop-filter: blur(50px);
        border-radius: var(--CornerSmall, 8px);
      }

      .logo-section {
        display: flex;
        align-items: center;
        justify-content: start;
        z-index: 1;
        background: var(--paper);
        padding: 10px;
        border-radius: 0%;
        height: 13vh;
        width: auto;
        flex-shrink: 0;
      }

      .pattern-top {
        width: 14rem;
        height: 11rem;
        position: fixed;
        top: -4rem;
        left: -1rem;
      }

      .pattern-bottom {
        width: 25rem;
        height: 11rem;
        position: fixed;
        bottom: 0;
        right: 0;
      }

      /* Responsive styles */
      @media (max-width: 768px) {
        .outer-container {
          margin: 0;
        }

        .content-wrapper {
          padding: 8px;
          box-shadow: none;
        }

        .logo-section {
          justify-content: center;
          background: var(--paper);
          height: 11vh;
          width: 100%;
        }

        .pattern-top,
        .pattern-bottom {
          display: none;
        }

        form {
          box-shadow: none !important;
          padding: 0 !important;
        }
      }

      @media (min-width: 769px) {
        .outer-container {
          margin: 2rem 0;
        }

        form {
          box-shadow: none !important;
          padding: 0 !important;
        }
      }

      /* Hide scrollbars */
      ::-webkit-scrollbar {
        display: none;
      }

      ::-webkit-scrollbar-thumb {
        display: none;
      }

      ::-webkit-scrollbar-track {
        display: none;
      }

      /* Scrollable grid container */
      .grid-container {
        flex: 1;
        padding: 2rem 7rem;
        min-height: 75vh;
        max-height: 75vh;
        display: flex;
        justify-content: center;
        overflow-y: auto;
      }

      @media (max-width: 768px) {
        .grid-container {
          padding: 0;
          min-height: auto;
          max-height: none;
        }
      }
    `
  ];

  @property({ type: Object })
  interfaceData: any = null;

  @property({ type: Object })
  pageGroup: any = null;

  @property({ type: String })
  view: string = 'main';

  @property({ type: Boolean })
  private isSmallScreen: boolean = false;

  firstUpdated() {
    // Check screen size initially
    this.checkScreenSize();
    
    // Add resize listener
    window.addEventListener('resize', this.checkScreenSize.bind(this));
  }

  disconnectedCallback() {
    // Remove resize listener
    window.removeEventListener('resize', this.checkScreenSize.bind(this));
    super.disconnectedCallback();
  }

  private checkScreenSize() {
    this.isSmallScreen = window.innerWidth <= 768;
    this.requestUpdate();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('view')) {
      this.dialogOpen = this.view === 'dialog';
      
      if (this.view === 'dialog') {
        document.body.classList.add('dialog-open');
      } else {
        document.body.classList.remove('dialog-open');
      }
      
      this.requestUpdate();
    }
  }

  private closeDialog() {
    this.hideSectionPage();
    
    this.dispatchEvent(new CustomEvent('view-change', {
      bubbles: true,
      composed: true,
      detail: { view: 'main' }
    }));
  }

  _viewList(_evt: any) {
    if (!this.dialogsStack.length) {
      this.view = 'list';
      this.updateLocationHash();
    } else {
      const args = this.dialogsStack.pop();
      const dialog = this.shadowRoot.querySelector('#dialog');

      dialog.payload = args[0];
      dialog.params = args[1];
      dialog.loading = false;
    }
  }

  render() {
    if (!this.interfaceData || !this.pageGroup) {
      return html`
        <div class="flex items-center justify-center h-full min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
          <div class="text-center p-8 max-w-md">
            <div class="relative mb-6">
              <div class="w-20 h-20 mx-auto bg-gradient-to-r from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/25 rotate-45 transform-gpu">
                <svg class="w-10 h-10 text-white -rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </div>
              <div class="absolute -inset-2 bg-rose-500/10 rounded-3xl blur-sm animate-pulse"></div>
            </div>

            <h2 class="text-2xl font-bold text-gray-900 mb-3 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Content Unavailable
            </h2>
            
            <p class="text-gray-600 mb-6 leading-relaxed">
              We couldn't load the interface data at this moment. This might be temporary - please try refreshing the page.
            </p>

              <button 
              onclick="window.location.href = '/';"
              class="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-slate-900 to-gray-800 text-white font-medium rounded-xl hover:from-gray-800 hover:to-slate-900 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform-gpu"
            >
              <svg class="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              Go to Homepage
              <div class="absolute inset-0 rounded-xl bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>

            <div class="mt-8 flex justify-center space-x-2">
              <div class="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
              <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style="animation-delay: 0.3s"></div>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <!-- Main Content with new design -->
      <div class="main-content" style="background: var(--default); min-height: 100vh; overflow-y: auto;">
        ${!this.isSmallScreen ? html`
          <img
            src="/static/dsv4.0/images/web/patternpad_top_mipay.png"
            alt="Pattern Pad Top"
            class="pattern-top"
          />
          <img
            src="/static/dsv4.0/images/web/patternpad_bottom_mipay.png"
            alt="Pattern Pad Bottom"
            class="pattern-bottom"
          />
        ` : ''}

        <div style="padding: 0; overflow: visible; margin: ${this.isSmallScreen ? '0px' : '2rem 4rem'};">
          <div class="outer-container">
            <div class="content-wrapper">
              <!-- Logo Section -->
              <div class="logo-section">
                <img
                  src="https://mipay.cash/media/administration_gateway_logo/mipay.png"
                  alt="Logo"
                  style="height: 36px; position: relative; left: ${this.isSmallScreen ? '0' : '1.8rem'}"
                />
              </div>

              <!-- Grid Container -->
              <div class="grid-container">
                <div class="w-full h-full overflow-y-auto">
                  ${this.pageGroup.pages.map((page: any) => html`        
                    <section class="w-full py-0" id=${page.id}>
                      <div>
                        <div class="w-full justify-center flex-wrap">
                          ${page._feedList.map((feed: any) => html`
                            <div class="${this._gridClasses(feed)} p-0">
                              <form-render 
                                .template="payments"
                                .feed="${feed}" 
                                .top="${this._computeTop()}" 
                                .params="${this.parseParams()}"
                                class="w-full"
                              ></form-render>
                            </div>
                          `)}
                        </div>
                      </div>
                    </section>
                  `)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Dialog Overlay (unchanged) -->
      ${this.view === 'dialog' ? html`
        <div class="dialog-backdrop" @click=${this.closeDialog}>
          <div class="custom-dialog" @click=${(e: Event) => e.stopPropagation()}>
            <section-page 
              id="dialog" 
              .isSectionPage=${true} 
              .closeDialog=${this.closeDialog} 
              @view-list=${this._viewList} 
              .queue=${this.dialogServicesQueue}
            ></section-page>
          </div>
        </div>
      ` : ''}
    `;
  }

  _gridClasses(feed: any): string {
    const sizeMap: { [key: string]: string } = {
      'is-full': 'col-span-full w-full',
      'is-half': 'md:col-span-6 col-span-full',
      'is-one-third': 'md:col-span-4 col-span-full',
      'is-two-thirds': 'md:col-span-8 col-span-full',
      'is-one-quarter': 'md:col-span-3 col-span-full',
      'is-three-quarters': 'md:col-span-9 col-span-full'
    };
    return sizeMap[feed.size] || 'col-span-full w-full';
  }

  private _computeTop(): number {
    return 0;
  }
}

customElements.define('payments-page', PaymentsPage as any);